import { z } from 'zod';
import { Status } from '@pillar-portal/database/dist/frontend';
import type {
    LogicalTerminalConfiguration,
    TerminalDeviceConfiguration,
    ProcessorSearchPageRequest,
    ProcessorSearchRequest,
    ProcessorListItem,
    ProcessorSearchPageResponse,
    CreateProcessorRequest,
    GetProcessorResponse,
    UpdateProcessorRequest,
    MerchantAccountListItem,
    MerchantAccountSearchListItem,
    MerchantAccountSearchPageRequest,
    MerchantAccountSearchRequest,
    MerchantAccountSearchPageResponse,
    CreateMerchantAccountRequest,
    GetMerchantAccountResponse,
    UpdateMerchantAccountRequest,
    LogicalTerminalSearchPageRequest,
    LogicalTerminalSearchRequest,
    LogicalTerminalSearchPageResponse,
    CreateLogicalTerminalRequest,
    UpdateLogicalTerminalRequest,
    GetLogicalTerminalResponse,
    TerminalDeviceSearchPageRequest,
    TerminalDeviceSearchRequest,
    CreateTerminalDeviceRequest,
    UpdateTerminalDeviceRequest,
    GetTerminalDeviceResponse,
    MerchantOrganizationSearchPageRequest,
    MerchantOrganizationSearchRequest,
    PreviewLogicalTerminalOrganizationChangeRequest,
} from '../payments_infrastructure.types';
import { MerchantAccountConfigurationSchema } from '../payments_infrastructure.types';
import { OpaqueCursorPaginationSchema } from '../authorization.types';
import { NonEmptyStringSchema, PositiveIntSchema } from './common.schemas';
import { NumericIpgSystemIdSchema } from './third-party-system.schemas';
import { BusinessDetailsSchema, ProcessorConfigSchema } from '../backend_config.types';
import {
    SearchScopeOrganizationUuidSchema,
    SearchTermsSchema,
} from './omnisearch.schemas';

export { MerchantAccountConfigurationSchema };

export const PaymentsInfrastructureLimitSchema: z.ZodType<number | undefined> = z.coerce
    .number()
    .int()
    .positive()
    .optional()
    .transform((limitValue: number | undefined) => {
        if (limitValue === undefined) {
            return undefined;
        }
        return Math.min(limitValue, 500);
    });

export const LogicalTerminalConfigurationSchema: z.ZodType<LogicalTerminalConfiguration> = z.object({
    allow_override_fee: z.boolean().optional(),
    surcharge_pct: z.number().min(0).max(100).optional(),
    convenience_fee: z.number().min(0).optional(),
});

export const TerminalDeviceConfigurationSchema: z.ZodType<TerminalDeviceConfiguration> = z.object({
    allow_override_fee: z.boolean().optional(),
    surcharge_pct: z.number().min(0).max(100).optional(),
    convenience_fee: z.number().min(0).optional(),
});

export const ProcessorListItemSchema: z.ZodType<ProcessorListItem> = z.object({
    processor_uuid: z.string().uuid(),
    processor_name: NonEmptyStringSchema,
    status: z.enum(Status),
});

export const ProcessorSearchPageRequestSchema: z.ZodType<ProcessorSearchPageRequest> = z.object({
    q: z.string().trim().min(1).optional(),
    limit: PaymentsInfrastructureLimitSchema,
    cursor: z.string().trim().min(1).optional(),
});

export const ProcessorSearchRequestSchema: z.ZodType<ProcessorSearchRequest> = z
    .object({
        terms: SearchTermsSchema,
        limit: PaymentsInfrastructureLimitSchema,
        cursor: z.string().trim().min(1).optional(),
        organization_uuid: SearchScopeOrganizationUuidSchema,
        show_deleted: z.boolean().optional(),
    })
    .strict();
export const ProcessorNameParamSchema: z.ZodType<string> = NonEmptyStringSchema;

export const ProcessorSearchPageResponseSchema: z.ZodType<ProcessorSearchPageResponse> = z.object({
    processors: z.array(ProcessorListItemSchema),
    pagination: OpaqueCursorPaginationSchema,
});

export const CreateProcessorRequestSchema: z.ZodType<CreateProcessorRequest> = z.object({
    processor_name: NonEmptyStringSchema,
    processor_config: ProcessorConfigSchema,
});

export const GetProcessorResponseSchema: z.ZodType<GetProcessorResponse> = z.object({
    processor_uuid: z.string().uuid(),
    processor_name: NonEmptyStringSchema,
    status: z.enum(Status),
    processor_config: ProcessorConfigSchema,
});

export const UpdateProcessorRequestSchema: z.ZodType<UpdateProcessorRequest> = z.object({
    processor_name: NonEmptyStringSchema.optional(),
    processor_config: ProcessorConfigSchema.optional(),
    status: z.enum(Status).optional(),
}).strict().refine(
    (value: UpdateProcessorRequest) =>
        value.processor_name !== undefined ||
        value.processor_config !== undefined ||
        value.status !== undefined,
    {
        message: 'At least one processor field must be provided.',
    }
);

const MerchantAccountSearchListItemSchemaBase = z.object({
    merchant_account_uuid: z.string().uuid(),
    processor_name: NonEmptyStringSchema,
    processor_mid: NonEmptyStringSchema,
    organization_uuid: z.string().uuid(),
    organization_name: NonEmptyStringSchema,
    businessDetails: BusinessDetailsSchema.optional(),
    applies_to_children: z.boolean(),
    status: z.enum(Status),
    allow_surcharge: z.boolean(),
    surcharge_pct: z.number().min(0).max(100),
    duplicate_processor_mid_count: z.number().int().min(1),
    has_duplicate_processor_mid: z.boolean(),
});
export const MerchantAccountSearchListItemSchema: z.ZodType<MerchantAccountSearchListItem> =
    MerchantAccountSearchListItemSchemaBase;

const MerchantAccountListItemSchemaBase = MerchantAccountSearchListItemSchemaBase.extend({
    merchant_account_id: PositiveIntSchema,
    organization_id: PositiveIntSchema,
});
export const MerchantAccountListItemSchema: z.ZodType<MerchantAccountListItem> =
    MerchantAccountListItemSchemaBase;

export const MerchantAccountSearchPageRequestSchema: z.ZodType<MerchantAccountSearchPageRequest> =
    z.object({
        q: z.string().trim().min(1).optional(),
        limit: PaymentsInfrastructureLimitSchema,
        cursor: z.string().trim().min(1).optional(),
        duplicates_only: z.boolean().optional(),
        merchant_organization_uuid: z.string().uuid().optional(),
    }).strict();

export const MerchantAccountSearchRequestSchema: z.ZodType<MerchantAccountSearchRequest> = z
    .object({
        terms: SearchTermsSchema,
        limit: PaymentsInfrastructureLimitSchema,
        cursor: z.string().trim().min(1).optional(),
        duplicates_only: z.boolean().optional(),
        show_deleted: z.boolean().optional(),
        merchant_organization_uuid: z.string().uuid().optional(),
        organization_uuid: SearchScopeOrganizationUuidSchema,
        include_descendants: z.boolean().optional(),
    })
    .strict();
export const MerchantAccountIdParamSchema: z.ZodType<number> = PositiveIntSchema;
export const MerchantAccountUuidParamSchema: z.ZodType<string> = z.string().uuid();

export const MerchantAccountSearchPageResponseSchema: z.ZodType<MerchantAccountSearchPageResponse> =
    z.object({
        merchant_accounts: z.array(MerchantAccountSearchListItemSchema),
        pagination: OpaqueCursorPaginationSchema,
        duplicate_summary: z.object({
            has_duplicates: z.boolean(),
            duplicate_records_count: z.number().int().min(0),
        }),
    });

export const CreateMerchantAccountRequestSchema: z.ZodType<CreateMerchantAccountRequest> = z.object({
    processor_name: NonEmptyStringSchema,
    processor_mid: NonEmptyStringSchema,
    organization_uuid: z.string().uuid(),
    merchant_account_configuration: MerchantAccountConfigurationSchema,
}).strict();

export const UpdateMerchantAccountRequestSchema: z.ZodType<UpdateMerchantAccountRequest> = z.object({
    processor_name: NonEmptyStringSchema.optional(),
    processor_mid: NonEmptyStringSchema.optional(),
    applies_to_children: z.boolean().optional(),
    status: z.enum(Status).optional(),
    merchant_account_configuration: MerchantAccountConfigurationSchema.optional(),
}).strict();

export const GetMerchantAccountResponseSchema: z.ZodType<GetMerchantAccountResponse> =
    MerchantAccountSearchListItemSchemaBase.extend({
        merchant_account_configuration: MerchantAccountConfigurationSchema,
    });

export const LogicalTerminalListItemSchema = z.object({
    logical_terminal_uuid: z.string().uuid(),
    merchant_account_uuid: z.string().uuid(),
    name: z.string().nullable(),
    ipg_system_id: NumericIpgSystemIdSchema,
    processor_name: NonEmptyStringSchema,
    processor_mid: NonEmptyStringSchema,
    processor_tid: NonEmptyStringSchema,
    businessDetails: BusinessDetailsSchema.optional(),
    applies_to_children: z.boolean().nullable(),
    status: z.enum(Status),
    duplicate_processor_tid_count: z.number().int().min(1),
    has_duplicate_processor_tid: z.boolean(),
});

export const LogicalTerminalSearchPageRequestSchema: z.ZodType<LogicalTerminalSearchPageRequest> =
    z.object({
        q: z.string().trim().min(1).optional(),
        limit: PaymentsInfrastructureLimitSchema,
        cursor: z.string().trim().min(1).optional(),
        duplicates_only: z.boolean().optional(),
        merchant_account_uuid: z.string().uuid().optional(),
    }).strict();

export const LogicalTerminalSearchRequestSchema: z.ZodType<LogicalTerminalSearchRequest> = z
    .object({
        terms: SearchTermsSchema,
        limit: PaymentsInfrastructureLimitSchema,
        cursor: z.string().trim().min(1).optional(),
        duplicates_only: z.boolean().optional(),
        show_deleted: z.boolean().optional(),
        merchant_account_uuid: z.string().uuid().optional(),
        organization_uuid: SearchScopeOrganizationUuidSchema,
    })
    .strict();
export const LogicalTerminalIdParamSchema: z.ZodType<number> = PositiveIntSchema;
export const LogicalTerminalUuidParamSchema: z.ZodType<string> = z.string().uuid();

export const LogicalTerminalSearchPageResponseSchema: z.ZodType<LogicalTerminalSearchPageResponse> = z.object({
    logical_terminals: z.array(LogicalTerminalListItemSchema),
    pagination: OpaqueCursorPaginationSchema,
    duplicate_summary: z.object({
        has_duplicates: z.boolean(),
        duplicate_records_count: z.number().int().min(0),
    }),
});

export const MerchantOrganizationMerchantAccountSummaryItemSchema = z.object({
    merchant_account_uuid: z.string().uuid(),
    processor_name: NonEmptyStringSchema,
    processor_mid: NonEmptyStringSchema,
    status: z.enum(Status),
    allow_surcharge: z.boolean(),
    surcharge_pct: z.number().min(0).max(100),
});

export const MerchantOrganizationListItemSchema = z.object({
    organization_uuid: z.string().uuid(),
    name: NonEmptyStringSchema,
    businessDetails: BusinessDetailsSchema.optional(),
    merchant_accounts: z.array(MerchantOrganizationMerchantAccountSummaryItemSchema),
});

export const MerchantOrganizationSearchPageRequestSchema: z.ZodType<MerchantOrganizationSearchPageRequest> =
    z.object({
        root_organization_uuid: z.string().uuid(),
        q: z.string().trim().min(1).optional(),
        limit: PaymentsInfrastructureLimitSchema,
        cursor: z.string().trim().min(1).optional(),
    });

export const MerchantOrganizationSearchRequestSchema: z.ZodType<MerchantOrganizationSearchRequest> =
    z
        .object({
            root_organization_uuid: z.string().uuid(),
            terms: SearchTermsSchema,
            limit: PaymentsInfrastructureLimitSchema,
            cursor: z.string().trim().min(1).optional(),
        })
        .strict();

export const MerchantOrganizationSearchPageResponseSchema = z.object({
    merchant_organizations: z.array(MerchantOrganizationListItemSchema),
    pagination: OpaqueCursorPaginationSchema,
});

export const PreviewLogicalTerminalOrganizationChangeRequestSchema: z.ZodType<PreviewLogicalTerminalOrganizationChangeRequest> =
    z.object({
        organization_uuid: z.string().uuid().nullable().optional(),
    }).strict();

export const LogicalTerminalOrganizationConflictItemSchema = z.object({
    terminal_device_uuid: z.string().uuid(),
    processor_device_id: NonEmptyStringSchema,
    organization_uuid: z.string().uuid(),
    organization_name: NonEmptyStringSchema,
});

export const PreviewLogicalTerminalOrganizationChangeResponseSchema = z.object({
    conflicts: z.array(LogicalTerminalOrganizationConflictItemSchema),
});

export const CreateLogicalTerminalRequestSchema: z.ZodType<CreateLogicalTerminalRequest> = z
    .object({
        merchant_account_uuid: z.string().uuid(),
        ipg_system_id: NumericIpgSystemIdSchema,
        name: z.string().trim().min(1).nullable().optional(),
        processor_tid: NonEmptyStringSchema,
        organization_uuid: z.string().uuid().nullable().optional(),
        logical_terminal_configuration: LogicalTerminalConfigurationSchema.optional(),
        create_default_physical_terminal_device: z.boolean().optional(),
        create_default_virtual_terminal_device: z.boolean().optional(),
    })
    .refine(
        (value: CreateLogicalTerminalRequest) =>
            !(
                value.create_default_physical_terminal_device === true &&
                value.create_default_virtual_terminal_device === true
            ),
        {
            message:
                'create_default_physical_terminal_device and ' +
                'create_default_virtual_terminal_device cannot both be true',
            path: ['create_default_virtual_terminal_device'],
        }
    )
    .strict();

export const UpdateLogicalTerminalRequestSchema: z.ZodType<UpdateLogicalTerminalRequest> = z.object({
    name: z.string().trim().min(1).nullable().optional(),
    processor_tid: NonEmptyStringSchema.optional(),
    organization_uuid: z.string().uuid().nullable().optional(),
    applies_to_children: z.boolean().nullable().optional(),
    clear_conflicting_terminal_device_organization_ids: z.boolean().optional(),
    status: z.enum(Status).optional(),
    logical_terminal_configuration: LogicalTerminalConfigurationSchema.optional(),
    credential_uuid: z.string().uuid().nullable().optional(),
}).strict();

export const GetLogicalTerminalResponseSchema: z.ZodType<GetLogicalTerminalResponse> =
    z.object({
        logical_terminal_uuid: z.string().uuid(),
        merchant_account_uuid: z.string().uuid(),
        name: z.string().nullable(),
        ipg_system_id: NumericIpgSystemIdSchema,
        processor_name: NonEmptyStringSchema,
        processor_mid: NonEmptyStringSchema,
        processor_tid: NonEmptyStringSchema,
        businessDetails: BusinessDetailsSchema.optional(),
        applies_to_children: z.boolean().nullable(),
        status: z.enum(Status),
        duplicate_processor_tid_count: z.number().int().min(1),
        has_duplicate_processor_tid: z.boolean(),
        organization_uuid: z.string().uuid().nullable(),
        logical_terminal_configuration: LogicalTerminalConfigurationSchema,
        credential_uuid: z.string().uuid().nullable(),
    });

export const TerminalDeviceListItemSchema = z.object({
    terminal_device_uuid: z.string().uuid(),
    logical_terminal_uuid: z.string().uuid(),
    name: z.string().nullable(),
    processor_name: NonEmptyStringSchema,
    processor_mid: NonEmptyStringSchema,
    processor_tid: NonEmptyStringSchema,
    processor_device_id: NonEmptyStringSchema,
    businessDetails: BusinessDetailsSchema.optional(),
    is_virtual: z.boolean(),
    status: z.enum(Status),
    duplicate_processor_device_id_count: z.number().int().min(1),
    has_duplicate_processor_device_id: z.boolean(),
});

export const TerminalDeviceSearchPageRequestSchema: z.ZodType<TerminalDeviceSearchPageRequest> =
    z.object({
        q: z.string().trim().min(1).optional(),
        limit: PaymentsInfrastructureLimitSchema,
        cursor: z.string().trim().min(1).optional(),
        duplicates_only: z.boolean().optional(),
        logical_terminal_uuid: z.string().uuid().optional(),
    }).strict();

export const TerminalDeviceSearchRequestSchema: z.ZodType<TerminalDeviceSearchRequest> = z
    .object({
        terms: SearchTermsSchema,
        limit: PaymentsInfrastructureLimitSchema,
        cursor: z.string().trim().min(1).optional(),
        duplicates_only: z.boolean().optional(),
        show_deleted: z.boolean().optional(),
        logical_terminal_uuid: z.string().uuid().optional(),
        organization_uuid: SearchScopeOrganizationUuidSchema,
    })
    .strict();
export const TerminalDeviceIdParamSchema: z.ZodType<number> = PositiveIntSchema;
export const TerminalDeviceUuidParamSchema: z.ZodType<string> = z.string().uuid();

export const TerminalDeviceSearchPageResponseSchema = z.object({
    terminal_devices: z.array(TerminalDeviceListItemSchema),
    pagination: OpaqueCursorPaginationSchema,
    duplicate_summary: z.object({
        has_duplicates: z.boolean(),
        duplicate_records_count: z.number().int().min(0),
    }),
});

export const CreateTerminalDeviceRequestSchema: z.ZodType<CreateTerminalDeviceRequest> = z
    .object({
        logical_terminal_uuid: z.string().uuid(),
        processor_device_id: NonEmptyStringSchema,
        is_virtual: z.boolean(),
        organization_uuid: z.string().uuid().nullable().optional(),
        terminal_device_configuration: TerminalDeviceConfigurationSchema.optional(),
    })
    .strict();

export const UpdateTerminalDeviceRequestSchema: z.ZodType<UpdateTerminalDeviceRequest> = z.object({
    name: z.string().trim().min(1).nullable().optional(),
    processor_device_id: NonEmptyStringSchema.optional(),
    is_virtual: z.boolean().optional(),
    organization_uuid: z.string().uuid().nullable().optional(),
    status: z.enum(Status).optional(),
    terminal_device_configuration: TerminalDeviceConfigurationSchema.optional(),
    credential_uuid: z.string().uuid().nullable().optional(),
}).strict();

export const GetTerminalDeviceResponseSchema: z.ZodType<GetTerminalDeviceResponse> =
    TerminalDeviceListItemSchema.extend({
        organization_uuid: z.string().uuid().nullable(),
        terminal_device_configuration: TerminalDeviceConfigurationSchema,
        credential_uuid: z.string().uuid().nullable(),
    });
